'use client';

import React, { useCallback, useRef, useState } from 'react';
import { ImageTooLargeError, prepareImageAttachment } from '@/lib/image-attachment';

export type ScreenshotValue = { name: string; contentType: string; dataUrl: string } | null;

interface ScreenshotInputProps {
  label: string;
  helper: string;
  dropHint: string;
  changeLabel: string;
  removeLabel: string;
  tooLargeMessage: string;
  onChange: (value: ScreenshotValue) => void;
}

import { exploreLabelClass } from '@/components/explore/explore-form-fields';

const labelClass = exploreLabelClass;

export const ScreenshotInput = ({
  label,
  helper,
  dropHint,
  changeLabel,
  removeLabel,
  tooLargeMessage,
  onChange,
}: ScreenshotInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ScreenshotValue>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFile = useCallback(
    async (file: File | undefined | null) => {
      if (!file) return;
      setError(null);
      setLoading(true);
      try {
        const attachment = await prepareImageAttachment(file);
        const value: ScreenshotValue = {
          name: attachment.name ?? file.name,
          contentType: attachment.contentType ?? file.type ?? 'image/jpeg',
          dataUrl: attachment.url as string,
        };
        setPreview(value);
        onChange(value);
      } catch (err) {
        if (err instanceof ImageTooLargeError) {
          setError(tooLargeMessage);
        } else {
          setError(tooLargeMessage);
        }
        setPreview(null);
        onChange(null);
      } finally {
        setLoading(false);
      }
    },
    [onChange, tooLargeMessage],
  );

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    void handleFile(e.dataTransfer.files?.[0]);
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
    onChange(null);
  };

  return (
    <div>
      <label className={labelClass}>{label}</label>

      {preview ? (
        <div className="flex items-center gap-4 rounded-nested border border-hairline bg-card p-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={preview.dataUrl}
            alt="Screenshot preview"
            className="h-16 w-16 shrink-0 rounded-nested border border-hairline object-cover"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-ink">{preview.name}</p>
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="mr-4 text-xs font-medium text-ink transition-colors hover:text-accent"
            >
              {changeLabel}
            </button>
            <button
              type="button"
              onClick={handleRemove}
              className="text-xs text-muted transition-colors hover:text-accent"
            >
              {removeLabel}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={() => inputRef.current?.click()}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          className={`flex cursor-pointer items-center justify-center gap-2 rounded-nested border border-dashed px-4 py-6 text-sm transition-colors duration-200 ${
            dragActive ? 'border-accent bg-accent-soft/40' : 'border-hairline hover:border-accent'
          }`}
        >
          {loading ? (
            <>
              <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-hairline border-t-accent motion-reduce:animate-none" />
              <span className="text-muted">Processing…</span>
            </>
          ) : (
            <span className="text-muted">{dropHint}</span>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => void handleFile(e.target.files?.[0])}
      />

      {error ? (
        <p className="mt-1.5 text-xs text-danger">{error}</p>
      ) : (
        <p className="mt-1.5 text-xs font-light text-muted">{helper}</p>
      )}
    </div>
  );
};
